/**
 * Base types and utilities for entity-specific admin handlers
 * 
 * This provides a reusable pattern for implementing admin functionality
 * for different entity types (Tags, Participants, Messages, etc.)
 */

import { useState, useCallback, useEffect, type Dispatch, type SetStateAction } from "react";
import type { UseAdminAppReturn } from "./useAdminApp";

/**
 * Base interface that all entity admin handlers must implement
 */
export interface EntityAdminHandler<TEntity, TCreateRequest = Partial<TEntity>, TUpdateRequest = Partial<TEntity>> {
  // State
  entities: TEntity[];
  loading: boolean;
  error: string | null;
  
  // State setters (for advanced usage)
  setEntities: Dispatch<SetStateAction<TEntity[]>>;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  
  // CRUD operations
  loadEntities: (params?: Record<string, string>) => Promise<void>;
  createEntity: (data: TCreateRequest) => Promise<TEntity>;
  updateEntity: (id: string, data: TUpdateRequest) => Promise<TEntity>;
  deleteEntity: (id: string) => Promise<void>;
  
  // Search/filter
  searchEntities?: (query: string, params?: Record<string, unknown>) => Promise<void>;
  
  // Refresh
  refresh: () => Promise<void>;
}

/**
 * Configuration for creating an entity admin handler
 */
export interface EntityAdminConfig<TEntity> {
  adminApp: UseAdminAppReturn;
  endpoint: string; // For LIST operations (usually plural: 'participants', 'tags')
  entityName: string;
  extractFromResponse?: (data: unknown) => TEntity[];
  // Optional: Override endpoints for CRUD operations if they differ from list endpoint
  crudEndpoint?: string; // For CREATE/UPDATE/DELETE (e.g., 'participant' when endpoint is 'participants')
  useIdInPath?: boolean; // true = /tags/:id (RESTful), false = /participant?id=xxx (query/body)
  /** Whether this entity requires workspace context. Platform-scoped entities (accounts, orgs, workspaces) set false. Default: true. */
  workspaceScoped?: boolean;
}

/**
 * Base hook for entity admin operations
 * Provides common CRUD functionality that can be extended
 */
export function useBaseEntityAdmin<TEntity extends { id: string }>(
  config: EntityAdminConfig<TEntity>
): Omit<EntityAdminHandler<TEntity>, 'searchEntities'> {
  const { 
    adminApp, 
    endpoint, 
    entityName, 
    extractFromResponse,
    crudEndpoint = endpoint, // Default to same as list endpoint
    workspaceScoped = true,
  } = config;
  const skipWs = !workspaceScoped ? { skipWorkspace: true } : undefined;
  
  const [entities, setEntities] = useState<TEntity[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadEntities = useCallback(async (params?: Record<string, string>) => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminApp.fetchEntities<TEntity>(endpoint, params, skipWs);
      const extracted = extractFromResponse ? extractFromResponse(data) : data;
      setEntities(extracted);
      console.log(`Loaded ${extracted.length} ${entityName}(s) from workspace: ${adminApp.workspaceId}`);
    } catch (err) {
      const errorMsg = `Failed to load ${entityName}s: ${err instanceof Error ? err.message : 'Unknown error'}`;
      setError(errorMsg);
      console.error(errorMsg);
      setEntities([]);
    } finally {
      setLoading(false);
    }
  }, [adminApp, endpoint, entityName, extractFromResponse]);

  const createEntity = useCallback(async (data: Partial<TEntity>): Promise<TEntity> => {
    try {
      const created = await adminApp.createEntity<TEntity>(crudEndpoint, data, undefined, skipWs);
      console.log(`Created ${entityName}:`, created.id);
      await loadEntities();
      return created;
    } catch (err) {
      const errorMsg = `Failed to create ${entityName}: ${err instanceof Error ? err.message : 'Unknown error'}`;
      console.error(errorMsg);
      throw new Error(errorMsg);
    }
  }, [adminApp, crudEndpoint, entityName, loadEntities]);

  const updateEntity = useCallback(async (id: string, data: Partial<TEntity>): Promise<TEntity> => {
    try {
      // For non-RESTful APIs, adminApp.updateEntity handles the pattern
      const updated = await adminApp.updateEntity<TEntity>(crudEndpoint, id, data, undefined, skipWs);
      console.log(`Updated ${entityName}:`, id);
      await loadEntities();
      return updated;
    } catch (err) {
      const errorMsg = `Failed to update ${entityName}: ${err instanceof Error ? err.message : 'Unknown error'}`;
      console.error(errorMsg);
      throw new Error(errorMsg);
    }
  }, [adminApp, crudEndpoint, entityName, loadEntities]);

  const deleteEntity = useCallback(async (id: string): Promise<void> => {
    try {
      await adminApp.deleteEntity(crudEndpoint, id, undefined, skipWs);
      console.log(`Deleted ${entityName}:`, id);
      await loadEntities();
    } catch (err) {
      const errorMsg = `Failed to delete ${entityName}: ${err instanceof Error ? err.message : 'Unknown error'}`;
      console.error(errorMsg);
      throw new Error(errorMsg);
    }
  }, [adminApp, crudEndpoint, entityName, loadEntities]);

  const refresh = useCallback(async () => {
    await loadEntities();
  }, [loadEntities]);

  // Auto-reload when workspace changes
  useEffect(() => {
    loadEntities();
  }, [adminApp.workspaceId]); // Only depend on workspaceId, not loadEntities to avoid loop

  return {
    entities,
    loading,
    error,
    setEntities,
    setLoading,
    setError,
    loadEntities,
    createEntity,
    updateEntity,
    deleteEntity,
    refresh,
  };
}

